/** Базовые элементы интерфейса.
 *
 * Осознанно небольшой набор: на Этапе 1 нужны формы, карточки и статусы.
 * Библиотека компонентов вырастет вместе с дашбордами (Этап 3), а сейчас
 * лишние абстракции только мешали бы менять вид экранов.
 */

import type { ReactNode } from 'react'

import { ApiError } from '@/lib/api'

export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      className={`animate-spin text-current ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"
      />
    </svg>
  )
}

export function PageLoader({ label = 'Загрузка…' }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-slate-500">
      <Spinner className="h-7 w-7" />
      <p className="text-sm">{label}</p>
    </div>
  )
}

/**
 * Показ ошибки пользователю.
 *
 * Всегда выводится `message` от сервера: он написан по-русски и предназначен
 * именно для показа. `request_id` показывается мелким шрифтом — с ним
 * поддержка находит запрос в логах, не переспрашивая «что вы делали».
 */
export function ErrorMessage({ error, action }: { error: unknown; action?: ReactNode }) {
  if (!error) return null

  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? 'Не удалось выполнить запрос. Проверьте соединение и попробуйте снова.'
        : 'Произошла неизвестная ошибка.'
  const requestId = error instanceof ApiError ? error.requestId : null

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <p>{message}</p>
      {action && <div className="mt-2">{action}</div>}
      {requestId && (
        <p className="mt-2 font-mono text-[11px] text-red-500">Код обращения: {requestId}</p>
      )}
    </div>
  )
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-red-600">{message}</p>
}

/** Заглушка для пустого состояния с подсказкой следующего шага. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="card flex flex-col items-center gap-3 p-10 text-center">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <p className="max-w-md text-sm text-slate-500">{description}</p>
      {action}
    </div>
  )
}

export function RoleBadge({ role }: { role: string }) {
  const styles: Record<string, string> = {
    owner: 'bg-brand-100 text-brand-800',
    admin: 'bg-violet-100 text-violet-800',
    editor: 'bg-emerald-100 text-emerald-800',
    viewer: 'bg-slate-100 text-slate-700',
    accountant: 'bg-amber-100 text-amber-800',
  }
  const titles: Record<string, string> = {
    owner: 'Владелец',
    admin: 'Администратор',
    editor: 'Редактор',
    viewer: 'Наблюдатель',
    accountant: 'Бухгалтер',
  }
  return (
    <span className={`badge ${styles[role] ?? 'bg-slate-100 text-slate-700'}`}>
      {titles[role] ?? role}
    </span>
  )
}

/** Полоса использования лимита. Приближение к лимиту должно быть заметно заранее. */
export function UsageBar({ used, limit }: { used: number; limit: number | null }) {
  if (limit === null) {
    return <span className="text-xs text-slate-500">без ограничений</span>
  }

  const ratio = limit === 0 ? 1 : Math.min(used / limit, 1)
  const color = ratio >= 1 ? 'bg-red-500' : ratio >= 0.8 ? 'bg-amber-500' : 'bg-brand-500'

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full ${color}`} style={{ width: `${ratio * 100}%` }} />
      </div>
      <span className="tabular text-xs text-slate-600">
        {used} / {limit}
      </span>
    </div>
  )
}
